import React, { memo, useMemo } from 'react';
import { createUseStyles, Text, Icon, Popover, Advice, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from 'lib/Web5Context';

import styles from './styles';

const useStyles = createUseStyles(styles, 'WidgetFooter');

const Footer = memo(({ widgetFilters, dashboardFilters, forceBIUtils, customDashboardFilters }) => {
    const classes = useStyles();
    const { t, getWeb4EntityFromString } = useWeb5Context();
    const theme = useTheme();

    const finalFilters = useMemo(() => {
        const { getUniqueFilters } = forceBIUtils;
        const wFilters = getUniqueFilters(widgetFilters);
        const dFilters = getUniqueFilters(dashboardFilters);

        const finalWFilters = wFilters.uiFilters.reduce((obj, current) => {
            if (!obj.applied) obj.applied = [];
            if (!obj.notApplied) obj.notApplied = [];
            if (!dFilters.uiFilterNames.includes(current.fullId)) obj.applied.push(current);
            else obj.notApplied.push(current);
            return obj;
        }, {});

        return { wFilters: finalWFilters || {}, dFilters: dFilters.uiFilters || [] };
    }, [forceBIUtils, widgetFilters, dashboardFilters]);

    const widgetFiltersPopover = useMemo(() => {
        const { wFilters = {} } = finalFilters;
        const { applied = [], notApplied = [] } = wFilters;

        return (
            <div className={classes.filtersPopover}>
                {applied.length > 0 && (
                    <>
                        <Text className={classes.filtersPopoverItem} type="subtitle" bold>
                            {t('label_applied_filters')}
                        </Text>
                        {applied.map((current, index) => {
                            return (
                                <Text
                                    key={index}
                                    className={classes.filtersPopoverItem}
                                    color={theme.colors.textLight.secondary}
                                >{`${current.description} (${t(current.entityLabel)})`}</Text>
                            );
                        })}
                    </>
                )}
                {notApplied.length > 0 && (
                    <>
                        {applied.length > 0 && <div className={classes.filtersPopoverDivider} />}
                        <Text className={classes.filtersPopoverItem} type="subtitle" bold>
                            {t('label_not_applied_filters')}
                        </Text>
                        <Advice type="info" showIcon defaultCollapsed={false}>
                            {t('label_not_applied_filters_description')}
                        </Advice>
                        {notApplied.map((current, index) => {
                            return (
                                <Text
                                    key={index}
                                    className={classes.filtersPopoverItem}
                                    color={theme.colors.textLight.secondary}
                                >{`${current.description} (${t(current.entityLabel)})`}</Text>
                            );
                        })}
                    </>
                )}
            </div>
        );
    }, [classes, finalFilters, t, theme]);

    const renderWidgetFilters = useMemo(() => {
        const { wFilters = {} } = finalFilters;
        const { applied = [], notApplied = [] } = wFilters;
        const totalFilters = [...applied, ...notApplied];
        const hasFilters = totalFilters.length !== 0;
        const iconName = hasFilters ? 'filterListActive' : 'filterList';

        const content = (
            <div
                className={classnames(classes.filtersContent, {
                    [classes.withFilters]: hasFilters,
                })}
            >
                <Icon name={iconName} />
                <Text type="caption">{`${totalFilters.length} ${t('label_widget')}`}</Text>
            </div>
        );
        return (
            <div className={classes.filtersContainerRight}>
                {totalFilters.length > 0 && (
                    <Popover content={widgetFiltersPopover} trigger={['hover']}>
                        {content}
                    </Popover>
                )}
                {totalFilters.length === 0 && content}
            </div>
        );
    }, [
        classes.filtersContainerRight,
        classes.filtersContent,
        classes.withFilters,
        finalFilters,
        t,
        widgetFiltersPopover,
    ]);

    const renderCustomDashboardFilters = useMemo(() => {
        if (!customDashboardFilters || !Object.keys(customDashboardFilters).length) return null;
        return Object.entries(customDashboardFilters).reduce((arr, [entity, filters]) => {
            if (!filters || !Object.entries(filters)) return arr;
            Object.entries(filters).forEach(([id, filter]) => {
                const newEntity = getWeb4EntityFromString(entity);
                arr.push(
                    <Text
                        key={id}
                        className={classes.filtersPopoverItem}
                        color={theme.colors.textLight.secondary}
                    >{`${t(newEntity.labels.singular)} ${id} (${t(newEntity.labels.plural)})`}</Text>,
                );
            });
            return arr;
        }, []);
    }, [customDashboardFilters, classes.filtersPopoverItem, getWeb4EntityFromString, t, theme]);

    const dashboardFiltersPopover = useMemo(() => {
        const { dFilters = [] } = finalFilters;
        return (
            <div className={classes.filtersPopover}>
                <Text className={classes.filtersPopoverItem} type="subtitle" bold>
                    {t('label_applied_filters')}
                </Text>
                {renderCustomDashboardFilters}
                {dFilters.map((current, index) => {
                    return (
                        <Text
                            key={index}
                            className={classes.filtersPopoverItem}
                            color={theme.colors.textLight.secondary}
                        >{`${current.description} (${t(current.entityLabel)})`}</Text>
                    );
                })}
            </div>
        );
    }, [
        classes.filtersPopover,
        classes.filtersPopoverItem,
        finalFilters,
        t,
        renderCustomDashboardFilters,
        theme,
    ]);

    const totalCustomDashboardFilters = useMemo(() => {
        if (!customDashboardFilters || !Object.keys(customDashboardFilters).length) return 0;
        return Object.entries(customDashboardFilters).reduce((num, [entity, filters]) => {
            if (!filters || !Object.entries(filters)) return 0;
            Object.entries(filters).forEach(([id, filter]) => {
                num++;
            });
            return num;
        }, 0);
    }, [customDashboardFilters]);

    const renderDashboardFilters = useMemo(() => {
        const { dFilters = [] } = finalFilters;
        const hasFilters = dFilters.length + totalCustomDashboardFilters !== 0;
        const iconName = hasFilters ? 'filterListActive' : 'filterList';
        const totalDashboardFilters = dFilters.length + totalCustomDashboardFilters;
        const content = (
            <div
                className={classnames(classes.filtersContent, {
                    [classes.withFilters]: hasFilters,
                })}
            >
                <Icon name={iconName} />
                <Text type="caption">{`${totalDashboardFilters} ${t('label_dashboard')}`}</Text>
            </div>
        );
        return (
            <div className={classes.filtersContainerLeft}>
                {totalDashboardFilters > 0 && (
                    <Popover content={dashboardFiltersPopover} trigger={['hover']}>
                        {content}
                    </Popover>
                )}
                {totalDashboardFilters === 0 && content}
            </div>
        );
    }, [
        classes.filtersContainerLeft,
        classes.filtersContent,
        classes.withFilters,
        finalFilters,
        t,
        dashboardFiltersPopover,
        totalCustomDashboardFilters,
    ]);

    const renderFilters = useMemo(() => {
        return (
            <>
                {renderDashboardFilters}
                <div className={classes.divider} />
                {renderWidgetFilters}
            </>
        );
    }, [renderWidgetFilters, renderDashboardFilters, classes.divider]);

    return <div className={classes.root}>{renderFilters}</div>;
});

export default Footer;
